import React, { useState, useEffect } from 'react';

// Custom icons to match the theme
const Icons = {
    Plus: (props) => (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
            <path d="M12 5v14M5 12h14" />
        </svg>
    ),
    Edit: (props) => (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
            <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
        </svg>
    ),
    Trash: (props) => (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
        </svg>
    ),
    CheckCircle: (props) => (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
            <path d="M22 11.08V12a10 10 0 1 1-5.93-8.56" />
            <path d="M22 4L12 14.01l-3-3" />
        </svg>
    ),
    X: (props) => (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
    ),
    Tag: (props) => (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
            <path d="M20.5 12c-2.2 0-4-1.8-4-4s1.8-4 4-4 4 1.8 4 4-1.8 4-4 4z" />
            <path d="M14 12V3h2.5c2.2 0 4 1.8 4 4s-1.8 4-4 4H14z" />
            <path d="M12 12V3h2.5" />
            <path d="M18 12c2.2 0 4-1.8 4-4s-1.8-4-4-4-4 1.8-4 4 1.8 4 4 4z" />
            <path d="M14 12V3" />
        </svg>
    )
};

const CategoriesPage = () => {
    const [categories, setCategories] = useState([]);
    const [newCategoryName, setNewCategoryName] = useState('');
    const [editCategoryId, setEditCategoryId] = useState(null);
    const [editCategoryName, setEditCategoryName] = useState('');
    const [statusMessage, setStatusMessage] = useState(null);
    const [messageType, setMessageType] = useState('success');
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [categoryToDelete, setCategoryToDelete] = useState(null);

    // The base API URL you provided
    const API_URL = process.env.REACT_APP_API_URL;

    const showMessage = (message, type = 'success') => {
        setStatusMessage(message);
        setMessageType(type);
        setTimeout(() => setStatusMessage(null), 5000);
    };

    useEffect(() => {
        fetchCategories();
    });

    const fetchCategories = async () => {
        try {
            const response = await fetch(`${API_URL}/productCategories`);
            if (!response.ok) {
                throw new Error('Failed to fetch categories');
            }
            const data = await response.json();

            if (Array.isArray(data)) {
                setCategories(data);
            } else if (data && Array.isArray(data.categories)) {
                setCategories(data.categories);
            } else {
                console.warn('API response is not in the expected format:', data);
                setCategories([]);
            }
        } catch (error) {
            console.error('Error fetching categories:', error);
            setCategories([]);
            showMessage('Error fetching categories: ' + error.message, 'error');
        }
    };

    const handleAddCategory = async (e) => {
        e.preventDefault();
        if (!newCategoryName.trim()) {
            showMessage('Category name cannot be empty.', 'error');
            return;
        }

        try {
            const response = await fetch(`${API_URL}/productCategories`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
                },
                body: JSON.stringify({ categoryName: newCategoryName }),
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to add category');
            }

            const data = await response.json();

            if (data && data.category && data.category.categoryId) {
                setCategories([...categories, data.category]);
                setNewCategoryName('');
                showMessage('Category added successfully!');
            } else {
                console.error('API response for adding is invalid:', data);
                showMessage('Failed to add category due to invalid response.', 'error');
            }
        } catch (error) {
            console.error('Error adding category:', error);
            showMessage('Error adding category: ' + error.message, 'error');
        }
    };

    const handleDeleteCategory = (categoryId) => {
        setCategoryToDelete(categoryId);
        setIsDeleteModalOpen(true);
    };

    const confirmDelete = async () => {
        if (!categoryToDelete) return;

        try {
            const response = await fetch(`${API_URL}/productCategories/${categoryToDelete}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
                },
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to delete category');
            }

            setCategories(categories.filter(cat => cat.categoryId !== categoryToDelete));
            showMessage('Category deleted successfully!');

        } catch (error) {
            console.error('Error deleting category:', error);
            showMessage('Error deleting category: ' + error.message, 'error');
        } finally {
            setIsDeleteModalOpen(false);
            setCategoryToDelete(null);
        }
    };

    const handleUpdateCategory = async (e) => {
        e.preventDefault();
        if (!editCategoryName.trim()) {
            showMessage('Category name cannot be empty.', 'error');
            return;
        }
        if (!editCategoryId) return;

        try {
            const response = await fetch(`${API_URL}/productCategories/${editCategoryId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
                },
                body: JSON.stringify({ categoryName: editCategoryName }),
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to update category');
            }
            const data = await response.json();
            setCategories(categories.map(cat =>
                cat.categoryId === data.category.categoryId ? data.category : cat
            ));
            setEditCategoryId(null);
            setEditCategoryName('');
            showMessage('Category updated successfully!');
        } catch (error) {
            console.error('Error updating category:', error);
            showMessage('Error updating category: ' + error.message, 'error');
        }
    };

    return (
        <div className="min-h-screen bg-gray-100 font-sans p-8 text-gray-900">
            <div className="max-w-8xl mx-auto">
                <header className="mb-8">
                    <h1 className="text-4xl font-extrabold text-gray-900">
                        Gestion des Catégories 🏷️
                    </h1>
                    <p className="mt-2 text-lg text-gray-500">
                        Ajouter, éditer ou supprimer des catégories de produits.
                    </p>
                </header>

                {statusMessage && (
                    <div className={`border px-4 py-3 rounded-xl mb-6 text-sm flex items-center space-x-2 ${messageType === 'success' ? 'bg-green-100 border-green-400 text-green-700' : 'bg-red-100 border-red-400 text-red-700'}`} role="alert">
                        <Icons.CheckCircle className="text-green-500" />
                        <span>{statusMessage}</span>
                    </div>
                )}

                <div className="bg-white p-6 rounded-xl shadow-lg mb-8">
                    <h2 className="text-xl font-semibold text-gray-800 mb-4">
                        Ajouter une nouvelle catégorie
                    </h2>
                    <form onSubmit={handleAddCategory} className="flex space-x-4">
                        <div className="relative flex-1">
                            <Icons.Tag className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                value={newCategoryName}
                                onChange={(e) => setNewCategoryName(e.target.value)}
                                placeholder="Nom de la nouvelle catégorie"
                                className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-400 transition-all duration-200"
                            />
                        </div>
                        <button
                            type="submit"
                            className="flex items-center space-x-2 px-6 py-3 bg-indigo-500 text-white font-semibold rounded-xl hover:bg-indigo-600 transition-colors"
                        >
                            <Icons.Plus className="w-5 h-5" />
                            <span>Ajouter</span>
                        </button>
                    </form>
                </div>

                <hr className="my-8 border-gray-300" />

                <div className="bg-white p-6 rounded-xl shadow-lg">
                    <h2 className="text-xl font-semibold text-gray-800 mb-4">
                        Catégories existantes
                    </h2>
                    <div className="overflow-x-auto">
                        <table className="min-w-full table-auto">
                            <thead>
                                <tr className="bg-gray-50 text-gray-600 uppercase text-sm font-semibold">
                                    <th className="py-3 px-6 text-left rounded-tl-xl">Nom de la Catégorie</th>
                                    <th className="py-3 px-6 text-center rounded-tr-xl">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="text-gray-600 text-sm font-light divide-y divide-gray-200">
                                {categories.length > 0 ? (
                                    categories.map((category) => (
                                        <tr key={category.categoryId} className="hover:bg-gray-50 transition-colors duration-200">
                                            <td className="py-3 px-6 text-left whitespace-nowrap">
                                                {editCategoryId === category.categoryId ? (
                                                    <form onSubmit={handleUpdateCategory}>
                                                        <input
                                                            type="text"
                                                            value={editCategoryName}
                                                            onChange={(e) => setEditCategoryName(e.target.value)}
                                                            className="w-full px-2 py-1 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                                                        />
                                                    </form>
                                                ) : (
                                                    <span className="font-medium text-gray-800">{category.categoryName}</span>
                                                )}
                                            </td>
                                            <td className="py-3 px-6 text-center">
                                                <div className="flex justify-center space-x-2">
                                                    {editCategoryId === category.categoryId ? (
                                                        <>
                                                            <button
                                                                type="submit"
                                                                onClick={handleUpdateCategory}
                                                                className="p-2 rounded-full text-green-500 hover:bg-green-100 transition-colors"
                                                                title="Enregistrer"
                                                            >
                                                                <Icons.CheckCircle className="w-5 h-5" />
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => setEditCategoryId(null)}
                                                                className="p-2 rounded-full text-gray-500 hover:bg-gray-100 transition-colors"
                                                                title="Annuler"
                                                            >
                                                                <Icons.X className="w-5 h-5" />
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <button
                                                                onClick={() => {
                                                                    setEditCategoryId(category.categoryId);
                                                                    setEditCategoryName(category.categoryName);
                                                                }}
                                                                className="p-2 rounded-full text-yellow-500 hover:bg-yellow-100 transition-colors"
                                                                title="Éditer"
                                                            >
                                                                <Icons.Edit className="w-5 h-5" />
                                                            </button>
                                                            <button
                                                                onClick={() => handleDeleteCategory(category.categoryId)}
                                                                className="p-2 rounded-full text-red-500 hover:bg-red-100 transition-colors"
                                                                title="Supprimer"
                                                            >
                                                                <Icons.Trash className="w-5 h-5" />
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="2" className="py-10 text-center text-gray-500">
                                            Aucune catégorie n'est encore enregistrée.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Confirmation Modal Component */}
            {isDeleteModalOpen && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center z-50">
                    <div className="relative p-8 bg-white rounded-lg shadow-xl w-96">
                        <h3 className="text-xl font-bold text-gray-900">Confirmer la suppression</h3>
                        <p className="mt-4 text-gray-600">
                            Êtes-vous sûr de vouloir supprimer cette catégorie ? Cette action est irréversible.
                        </p>
                        <div className="mt-6 flex justify-end space-x-4">
                            <button
                                onClick={() => {
                                    setIsDeleteModalOpen(false);
                                    setCategoryToDelete(null);
                                }}
                                className="px-4 py-2 text-sm font-semibold text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
                            >
                                Annuler
                            </button>
                            <button
                                onClick={confirmDelete}
                                className="px-4 py-2 text-sm font-semibold text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors"
                            >
                                Supprimer
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CategoriesPage;